import React from 'react';
import { MessageSquare, Trash2, Loader2 } from 'lucide-react';
import { format } from './content.fallback';

/**
 * Past conversations for the signed-in user.
 *
 * Presentational — loading and fetching live in AiAssistant so the list can be
 * refreshed without remounting.
 */

/**
 * Relative time, matching how chat apps label threads. Falls back to a date
 * once "days ago" stops being useful.
 */
const relativeTime = (value, copy) => {
    if (!value) return '';

    const then = new Date(value);
    if (Number.isNaN(then.getTime())) return '';

    const seconds = Math.round((Date.now() - then.getTime()) / 1000);

    if (seconds < 60) return copy.just_now;
    if (seconds < 3600) {
        return format(copy.minutes_ago, { count: Math.floor(seconds / 60) });
    }
    if (seconds < 86400) {
        return format(copy.hours_ago, { count: Math.floor(seconds / 3600) });
    }
    if (seconds < 604800) {
        return format(copy.days_ago, { count: Math.floor(seconds / 86400) });
    }
    return then.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year:
            then.getFullYear() === new Date().getFullYear()
                ? undefined
                : 'numeric',
    });
};

const HistoryList = ({
    copy,
    conversations,
    isLoading,
    error,
    activeConversationId,
    onSelect,
    onDelete,
    deletingId,
}) => {
    if (isLoading) {
        return (
            <div className="sai-history__state">
                <Loader2 size={18} className="sai-spin" aria-hidden="true" />
                <span>{copy.loading}</span>
            </div>
        );
    }

    if (error) {
        return <div className="sai-history__state sai-history__state--error">{error}</div>;
    }

    if (!conversations?.length) {
        return (
            <div className="sai-history__state">{copy.empty}</div>
        );
    }

    return (
        <div className="sai-history">
            {conversations.map((item) => {
                const isActive = item.conversation_id === activeConversationId;
                const isDeleting = deletingId === item.conversation_id;

                return (
                    <div
                        key={item.conversation_id}
                        className={`sai-history__item${
                            isActive ? ' sai-history__item--active' : ''
                        }`}
                    >
                        <button
                            type="button"
                            className="sai-history__open"
                            onClick={() => onSelect(item.conversation_id)}
                            disabled={isDeleting}
                            title={item.title}
                        >
                            <span className="sai-history__icon">
                                <MessageSquare size={15} aria-hidden="true" />
                            </span>
                            <span className="sai-history__body">
                                <span className="sai-history__title">{item.title}</span>
                                <span className="sai-history__meta">
                                    Tokens: {item.token_usgae ?? 'Unavailable'} · Cost: {item.cost != null ? `$${item.cost} USD` : 'Unavailable'}
                                </span>
                                <span className="sai-history__meta">
                                    {relativeTime(item.last_message_at, copy)}
                                    {item.message_count
                                        ? ` · ${format(copy.messages_suffix, {
                                              count: item.message_count,
                                          })}`
                                        : ''}
                                </span>
                            </span>
                        </button>

                        <button
                            type="button"
                            className="sai-history__delete"
                            onClick={() => onDelete(item.conversation_id)}
                            disabled={isDeleting}
                            aria-label={format(copy.delete_aria, {
                                title: item.title,
                            })}
                            title={copy.delete_tooltip}
                        >
                            {isDeleting ? (
                                <Loader2 size={15} className="sai-spin" aria-hidden="true" />
                            ) : (
                                <Trash2 size={15} aria-hidden="true" />
                            )}
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

export default HistoryList;
